
import { Instructors } from "@/api";
import { Gender, Instructor, Service } from "@/api/types";
import GoogleAddressAutocomplete from "@/components/common/GoogleAddressAutocomplete.vue";
import PageTitle from "@/components/common/PageTitle.vue";
import PhoneInput from "@/components/common/PhoneInput.vue";
import TitleButtons from "@/components/common/TitleButtons.vue";
import InstructorCoursesList from "@/components/courses/InstructorCoursesList.vue";
import { PhoneCodes } from "@/mixins/countryCodes.mixin";
import { GooglePlace } from "@/utils/googleMaps";
import moment from "moment";
import { toSnake } from "snake-camel";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";

const Auth = namespace("auth");

@Component({
  components: {
    GoogleAddressAutocomplete,
    InstructorCoursesList,
    PageTitle,
    TitleButtons,
    ValidationProvider,
    ValidationObserver,
    PhoneInput
  },
  mixins: [PhoneCodes]
})
export default class EditInstructorsPage extends Vue {
  @Auth.Getter("getUser") getUser!: any;

  menuBirthday = false;
  menuVacationStart = false;
  menuVacationEnd = false;
  signatureButtonsArray = [
    {
      text: "Retake",
      icon: "mdi-pencil-box-outline",
      color: "white",
      action: "clear",
      activeClass: "",
      disabled: false
    },
    {
      text: "Sign",
      icon: "mdi-checkbox-marked-circle-outline",
      color: "dark",
      action: "save",
      activeClass: "",
      disabled: false
    }
  ];
  buttonsArray = [
    {
      text: "Back",
      icon: "mdi-chevron-left",
      color: "white",
      action: "back"
    },
    {
      text: "Sign",
      icon: "mdi-checkbox-marked-circle-outline",
      color: "gray",
      action: "signature"
    },
    {
      text: "Save",
      icon: "mdi-pencil-box-outline",
      color: "dark",
      action: "save"
    }
  ];
  headersTitle = [
    { icon: "mdi-account-box", header: "Instructor Profile Editor" }
  ];
  instructor: Instructor = {
    id: null,
    name: "",
    email: "",
    phone: "",
    genderId: null,
    workingHourFrom: "",
    workingHourTo: "",
    serviceId: null,
    outsourceCost: "",
    payDirectly: false,
    vacantionFrom: "",
    vacantionTo: "",
    signature: "",
    teta: "",
    calculateTravelFromHome: false,
    homeAddress: "",
    homePlaceId: null
  };
  servicesAndGenders: { genders: Array<Gender>; services: Array<Service> } = {
    services: [],
    genders: []
  };
  bankDetails = {
    bankName: "",
    accountHolder: "",
    accountNumber: "",
    branchCode: ""
  };
  /** What the bank form last loaded or saved, so Save knows if it changed. */
  private loadedBankDetails = JSON.stringify(this.bankDetails);
  bankHeaders = [{ icon: "", header: "Instructor bank details" }];
  savingBankDetails = false;

  vacationStart = moment().format("YYYY-MM-DD");
  vacationEnd = moment().format("YYYY-MM-DD");
  get instructorId() {
    return this.$route.params.id;
  }
  get canManageBankDetails(): boolean {
    return Boolean(
      this.getUser &&
        this.getUser.permissions &&
        this.getUser.permissions.includes("manage_instructor_bank_details")
    );
  }
  /** The address last picked from Google, so a freehand edit can drop the id. */
  private selectedHomeAddress = "";

  get homeAddressRules(): string {
    return this.instructor.calculateTravelFromHome ? "required" : "";
  }

  onHomeAddressSelect(place: GooglePlace): void {
    this.instructor.homeAddress = place.address;
    this.instructor.homePlaceId = place.placeId;
    this.selectedHomeAddress = place.address;
  }

  @Watch("instructor.homeAddress")
  onHomeAddressChange(value: string): void {
    // Typing over a picked address invalidates its place id — the distance
    // lookup must not keep resolving the place that was abandoned.
    if (value !== this.selectedHomeAddress) {
      this.instructor.homePlaceId = null;
    }
  }

  get outsourceRules() {
    return this.isOutsource ? "required" : "";
  }
  get vacationStartDateFormatted() {
    return moment(this.vacationStart).format("DD/MM/YYYY");
  }
  get vacationEndDateFormatted() {
    return moment(this.vacationEnd).format("DD/MM/YYYY");
  }
  get isPermanent() {
    return !this.isOutsource;
  }
  get isOutsource() {
    const service = this.servicesAndGenders.services.find(
      item => item.id === this.instructor.serviceId
    );
    return Boolean(service && service.name.toLowerCase() === "outsource");
  }
  get gender() {
    return this.servicesAndGenders.genders.find(
      (item: Gender) => item.id === this.instructor.genderId
    )?.name;
  }
  get genders() {
    return this.servicesAndGenders.genders.map((item: Gender) => item.name);
  }
  async created() {
    try {
      [this.instructor, this.servicesAndGenders] = await Promise.all([
        Instructors.info(this.instructorId),
        Instructors.servicesAdnGenders()
      ]);
      // Seed the picked-address marker before the watcher flushes, so loading
      // an instructor does not look like a freehand edit and drop their place id.
      this.selectedHomeAddress = this.instructor.homeAddress || "";
      // An instructor with no vacation keeps today in the picker rather than
      // being shown "Invalid date".
      const vacationFrom = this.formatDate(this.instructor.vacantionFrom);
      const vacationTo = this.formatDate(this.instructor.vacantionTo);
      if (vacationFrom) {
        this.vacationStart = moment(vacationFrom).format("YYYY-MM-DD");
      }
      if (vacationTo) {
        this.vacationEnd = moment(vacationTo).format("YYYY-MM-DD");
      }
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors || err);
    }
    // Loaded on its own, because it used to sit at the end of the block above
    // and anything that threw earlier — a null vacation date did — skipped it
    // silently, leaving the saved details looking as though they had been lost.
    await this.loadBankDetails();
  }
  // The permission can land after this page is created, when the session is
  // restored from the API rather than from a fresh login.
  @Watch("canManageBankDetails")
  async onCanManageBankDetailsChange(): Promise<void> {
    await this.loadBankDetails();
  }
  async loadBankDetails(): Promise<void> {
    if (!this.canManageBankDetails) return;
    this.bankDetails = await Instructors.bankDetails(this.instructorId);
    this.loadedBankDetails = JSON.stringify(this.bankDetails);
  }
  /** Whether the bank form holds something other than what was loaded. */
  get bankDetailsChanged(): boolean {
    return JSON.stringify(this.bankDetails) !== this.loadedBankDetails;
  }
  /** Whether anything at all is typed in the bank form, as on a fresh instructor. */
  get bankDetailsFilled(): boolean {
    return Object.values(this.bankDetails).some(value => !!value);
  }
  /**
   * The bank details are typed on the same page as the profile and saved by
   * the same Save, so nothing typed is left behind to vanish on reload.
   */
  async saveBankDetails(quiet = false) {
    this.savingBankDetails = true;
    try {
      this.bankDetails = await Instructors.updateBankDetails(
        this.instructorId,
        {
          bankName: this.bankDetails.bankName,
          accountHolder: this.bankDetails.accountHolder,
          accountNumber: this.bankDetails.accountNumber,
          branchCode: this.bankDetails.branchCode
        }
      );
      this.loadedBankDetails = JSON.stringify(this.bankDetails);
      if (!quiet) {
        await this.$success(
          "<strong>Success!</strong> Bank details have been saved securely."
        );
      }
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors || err);
    } finally {
      this.savingBankDetails = false;
    }
  }
  onSign() {
    this.$modal.show("attend-modal");
    Vue.nextTick().then(() => {
      if (this.instructor.signature) {
        (this.$refs.signaturePad as Vue & {
          fromDataURL: (data: string) => void;
        }).fromDataURL(this.instructor.signature);
      }
    });
  }
  onCloseModal() {
    this.$modal.hide("attend-modal");
  }
  onClearSignature() {
    (this.$refs.signaturePad as Vue & {
      clearSignature: () => void;
    }).clearSignature();
  }
  async onSaveSignature() {
    const { isEmpty, data } = (this.$refs.signaturePad as Vue & {
      saveSignature: () => any;
    }).saveSignature();
    if (!isEmpty) {
      this.instructor.signature = data;
      this.onCloseModal();
    } else {
      await this.$dialog.error({
        text: "Signature should not be empty!",
        title: "Error",
        persistent: false,
        actions: {
          Yes: {
            color: "red",
            text: "OK"
          }
        }
      });
    }
  }
  async onGoBack() {
    await this.$router.push({ name: "Instructors" });
  }
  async onSave() {
    (this.$refs.form as Vue & {
      click: () => void;
    }).click();
  }

  /**
   * A dd-mm-yyyy date as mm/dd/yyyy for moment. Everything the API can
   * actually send has to survive this: a null comes back for every user who
   * has never been given a vacation, and splitting that threw, which aborted
   * created() on the line before the bank details were fetched.
   */
  formatDate(date: string | undefined | null): string {
    if (typeof date !== "string" || !date) {
      return "";
    }
    const [day, month, year] = date.split("-");
    if (!day || !month || !year) {
      return "";
    }
    return `${month}/${day}/${year}`;
  }
  async save() {
    const data = JSON.parse(JSON.stringify(this.instructor));
    delete data.permissions;
    delete data.roles;
    delete data.id;
    if (!data.signature) {
      delete data.signature;
    }
    if (!data.address) {
      delete data.address;
    }
    if (this.isPermanent) {
      delete data.outsourceCost;
      data.payDirectly = false;
    } else {
      data.outsourceCost = data.outsourceCost?.toString();
    }
    data.vacantionFrom = moment(this.vacationStart).format("DD-MM-YYYY");
    data.vacantionTo = moment(this.vacationEnd).format("DD-MM-YYYY");
    const reqData = toSnake(data);
    try {
      await Instructors.update(this.instructorId, reqData);
      if (
        this.canManageBankDetails &&
        (this.bankDetailsChanged || this.bankDetailsFilled)
      ) {
        await this.saveBankDetails(true);
      }
      await this.$success(
        "<strong>Success!</strong> Instructor has been updated!"
      );

      if (this.$route.name === "EditInstructor") {
        await this.$router.push({ name: "Instructors" });
      }
    } catch (e) {
      const err = e as any;
      (this.$refs.instructorForm as Vue & {
        setErrors: (errors: any) => void;
      }).setErrors(err.errors);
      await this.$error(err.errors);
    }
  }
}
